import React, { useState, useEffect, useRef } from 'react'
import styles from './StockDispatchPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { PageHeader, PageBody, TabBar, headerStyles } from '../components/PageHeader'
import { PalletsView } from './stock/PalletsView'
import { OrdersView } from './stock/OrdersView'
import { DispatchesView } from './stock/DispatchesView'
import { StockView } from './stock/StockView'
import { HeaderSlotContext } from './stock/chrome'
import { coverageFrom, dropUrlParams, urlParam, type Coverage, type StockRow } from './stock/stock'
import type { OrderRow } from './stock/orders'
import type { Load } from './stock/loads'

type TabType = 'stock' | 'orders' | 'dispatches'
type StockSubTab = 'stock' | 'pallets' | 'to-palletise'

// Where the Overview's tiles land (?tab=pallets, ?tab=to): read once, at mount.
const SUB_FROM_URL: Record<string, StockSubTab> = { pallets: 'pallets', to: 'to-palletise' }

// handoff §7n navigation: Stock, Orders and Dispatches are separate rail rows,
// so the screen comes from the route rather than a tab strip of its own. Only
// Stock carries header tabs (Stock · Pallets · To palletise): one ledger at
// two granularities. V4 titles the Stock screen "Stock & pallets".
const TITLES: Record<TabType, string> = { stock: 'Stock & pallets', orders: 'Orders', dispatches: 'Dispatches' }

export function StockDispatchPage({ view = 'stock' }: { view?: TabType }) {
  const activeTab = view
  const [activeStockSubTab, setActiveStockSubTab] = useState<StockSubTab>(() => SUB_FROM_URL[urlParam('tab') || ''] || 'stock')
  const [stock, setStock] = useState<StockRow[]>([])
  const [coverage, setCoverage] = useState<Coverage>({ state: 'checking' })
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [loads, setLoads] = useState<Load[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [asAt, setAsAt] = useState<Date | null>(null)
  // The tab whose data is currently shown (h 3994-4002); until it matches the
  // active tab, the table body shows skeleton rows.
  const [shown, setShown] = useState<string | null>(null)
  // Bumped when an order or load record changes something, so the list refetches.
  const [tick, setTick] = useState(0)
  // The header's action slot: Orders and Dispatches put New order / New load there.
  const [slot, setSlot] = useState<HTMLDivElement | null>(null)
  const { user } = useAuth()
  const seq = useRef(0)
  const coverageSeq = useRef(0)
  // The tabs read at least once. A tab whose first read failed has nothing to show: its list says so, never "none yet".
  const readTabs = useRef(new Set<string>())

  const readCoverage = (): Promise<Coverage> =>
    apiJson<unknown>(user, '/api/stock-receipts/coverage-gap').then(coverageFrom, (): Coverage => ({ state: 'failed' }))
  // Retry reads the coverage alone; the stock already shown stays as it is.
  const retryCoverage = async () => {
    const c = ++coverageSeq.current
    setCoverage({ state: 'failed', retrying: true })
    const next = await readCoverage()
    if (c === coverageSeq.current) setCoverage(next)
  }

  useEffect(() => {
    let n = 0
    const fetchData = async () => {
      try {
        setLoading(true)
        n = ++seq.current
        // The tab is named in the error so a failure says which list is missing.
        const load = <T,>(what: string, path: string) =>
          apiJson<T>(user, path).catch((err) => {
            throw new Error(`Failed to fetch ${what}: ${err instanceof Error ? err.message : String(err)}`)
          })
        if (activeTab === 'stock') {
          // Receipts outside coverage are read with the stock, so the list
          // never shows without them. A failed read of them still shows the
          // stock, with "could not be checked" and Retry: never as none.
          const [data, outside] = await Promise.all([
            load<StockRow[]>('stock', '/api/stock'),
            readCoverage(),
          ])
          if (n !== seq.current) return
          setStock(data)
          coverageSeq.current++ // a retry still in flight is older than this
          setCoverage(outside)
        } else if (activeTab === 'orders') {
          const data = await load<OrderRow[]>('orders', '/api/orders')
          if (n !== seq.current) return
          setOrders(data)
        } else if (activeTab === 'dispatches') {
          const data = await load<{ loads: Load[] }>('dispatches', '/api/dispatches')
          if (n !== seq.current) return
          setLoads(data.loads)
        }

        readTabs.current.add(activeTab)
        setError(null)
        setAsAt(new Date())
      } catch (err) {
        if (n === seq.current) setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        if (n === seq.current) setShown(activeTab)
        setLoading(false)
      }
    }

    if (user) {
      fetchData()
    }
  }, [activeTab, user, tick])
  // h 4167-4171: it fetches again on window focus and on reconnect.
  useRefetch(() => setTick((t) => t + 1))
  useEffect(() => dropUrlParams(['tab']), [])
  const showingLoading = shown !== activeTab
  const unread = !!error && !showingLoading && !readTabs.current.has(activeTab) && (activeTab !== 'stock' || activeStockSubTab === 'stock')
  // The Pallets and To palletise tabs are PalletsView's own fetch, not this one.
  const stamped = !showingLoading && (activeTab !== 'stock' || activeStockSubTab === 'stock')

  const renderStockContent = () => {
    if (activeStockSubTab === 'stock') {
      return (
        <StockView
          user={user}
          rows={stock}
          coverage={coverage}
          onRetryCoverage={retryCoverage}
          showingLoading={showingLoading}
          onChanged={() => setTick((t) => t + 1)}
        />
      )
    } else if (activeStockSubTab === 'pallets') {
      return <PalletsView key="register" user={user} tab="register" />
    } else {
      return <PalletsView key="to" user={user} tab="to" />
    }
  }

  const renderOrdersContent = () => <OrdersView user={user} orders={orders} onChanged={() => setTick((t) => t + 1)} showingLoading={showingLoading} />

  const renderDispatchesContent = () => <DispatchesView user={user} loads={loads} onChanged={() => setTick((t) => t + 1)} showingLoading={showingLoading} />

  const tabs = activeTab === 'stock' ? (
    <TabBar label="Stock view">
      <button
        className={activeStockSubTab === 'stock' ? headerStyles.tabOn : headerStyles.tab}
        onClick={() => setActiveStockSubTab('stock')}
        role="tab"
        aria-selected={activeStockSubTab === 'stock'}
      >
        Stock
      </button>
      <button
        className={activeStockSubTab === 'pallets' ? headerStyles.tabOn : headerStyles.tab}
        onClick={() => setActiveStockSubTab('pallets')}
        role="tab"
        aria-selected={activeStockSubTab === 'pallets'}
      >
        Pallets
      </button>
      <button
        className={activeStockSubTab === 'to-palletise' ? headerStyles.tabOn : headerStyles.tab}
        onClick={() => setActiveStockSubTab('to-palletise')}
        role="tab"
        aria-selected={activeStockSubTab === 'to-palletise'}
      >
        To palletise
      </button>
    </TabBar>
  ) : undefined

  return (
    <div className={styles.container}>
      <PageHeader
        title={TITLES[activeTab]}
        tabs={tabs}
        actions={activeTab === 'stock' ? undefined : <div ref={setSlot} style={{ display: 'contents' }} />}
      >
        {stamped && <AsAt at={asAt} />}
      </PageHeader>

      <PageBody>
        {error && (
          <div className={styles.error}>
            <p>{error}</p>
            <button type="button" onClick={() => setTick((t) => t + 1)}>
              Retry
            </button>
          </div>
        )}

        <HeaderSlotContext.Provider value={activeTab === 'stock' ? null : slot}>
          {unread ? null : activeTab === 'stock' ? renderStockContent() : activeTab === 'orders' ? renderOrdersContent() : renderDispatchesContent()}
        </HeaderSlotContext.Provider>
      </PageBody>
    </div>
  )
}
